import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../utils/axios';
import { OrderRequest, OrderResponse } from '../../../@types/order/order.type';

interface RequestState<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface OrderState {
  single: RequestState<OrderResponse>;
  create: RequestState<OrderResponse>;
  byOrganization: RequestState<OrderResponse[]>;
  updateStatus: RequestState<OrderResponse>;
}

const initialRequestState = <T,>(): RequestState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: OrderState = {
  single: initialRequestState<OrderResponse>(),
  create: initialRequestState<OrderResponse>(),
  byOrganization: initialRequestState<OrderResponse[]>(),
  updateStatus: initialRequestState<OrderResponse>(),
};

const BASE_URL = '/api/v1/orders';

// ---------- Thunks ----------
export const fetchOrderById = createAsyncThunk(
  'orders/fetchById',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`${BASE_URL}/${id}`);
      console.log('resdata: ', res.data)
      return res.data as OrderResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch failed');
    }
  }
);

export const createOrder = createAsyncThunk(
  'orders/create',
  async (data: OrderRequest, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(BASE_URL, data);
      return res.data as OrderResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Create failed');
    }
  }
);

export const fetchOrdersByOrganization = createAsyncThunk(
  'orders/fetchByOrganization',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(
        `${BASE_URL}/organization/${organizationId}?page=0&size=1000`
      );

      return res.data.content as OrderResponse[]; // Assuming response is Page<OrderResponse>
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch by organization failed');
    }
  }
);

export const fetchOrdersByParentOrganization = createAsyncThunk(
  'orders/fetchByParentOrganization',
  async (parentId: string, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(
        `${BASE_URL}/parent-organization/${parentId}?page=0&size=1000`
      );

      return res.data.content as OrderResponse[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Fetch by parent organization failed');
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateStatus',
  async ({ id, orderState }: { id: number; orderState: string }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.patch(`${BASE_URL}/${id}/status`, { orderState });
      return res.data as OrderResponse;
    } catch (err: any) {
      const headerError = err.response?.headers?.['x-error-message'];
      return rejectWithValue(headerError || err.response?.data?.message || 'Status update failed');
    }
  }
);

export const takeOrder = createAsyncThunk(
  'orders/take',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.patch(`/api/v1/voice-agent/orders/${id}/take`);
      return res.data;
    } catch (err: any) {
      if (err.response?.status === 409) return rejectWithValue('ALREADY_TAKEN');
      return rejectWithValue(err.response?.data?.message || 'Take failed');
    }
  }
);

export const releaseOrder = createAsyncThunk(
  'orders/release',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.patch(`/api/v1/voice-agent/orders/${id}/release`);
      return res.data as OrderResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Release failed');
    }
  }
);

export const cancelAgentRequest = createAsyncThunk(
  'orders/cancelAgentRequest',
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.patch(`/api/v1/voice-agent/orders/${id}/cancel-request`);
      return res.data as OrderResponse;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Cancel failed');
    }
  }
);

export const addProductToOrder = createAsyncThunk(
  'orders/addProduct',
  async ({ orderId, productId, quantity }: { orderId: number; productId: string; quantity: number }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/api/v1/ordered-products/order/${orderId}`, { productId, quantity });
      return undefined;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Add product failed');
    }
  }
);

export const updateOrderedProductQty = createAsyncThunk(
  'orders/updateOrderedProductQty',
  async ({ id, quantity }: { id: string; quantity: number }, { rejectWithValue }) => {
    try {
      await axiosInstance.patch(`/api/v1/ordered-products/${id}`, { quantity });
      return undefined;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Update qty failed');
    }
  }
);

export const removeOrderedProduct = createAsyncThunk(
  'orders/removeOrderedProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/v1/ordered-products/${id}`);
      return undefined;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Remove failed');
    }
  }
);

// ---------- Slice ----------
const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearCreateOrderState: (state) => {
      state.create = initialRequestState();
    },
  },
  extraReducers: (builder) => {
    // Fetch order by ID
    builder.addCase(fetchOrderById.pending, (state) => {
      state.single.loading = true;
      state.single.error = null;
    });
    builder.addCase(fetchOrderById.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload;
    });
    builder.addCase(fetchOrderById.rejected, (state, action) => {
      state.single.loading = false;
      state.single.error = action.payload as string;
    });

    // Create order
    builder.addCase(createOrder.pending, (state) => {
      state.create.loading = true;
      state.create.error = null;
    });
    builder.addCase(createOrder.fulfilled, (state, action) => {
      state.create.loading = false;
      state.create.data = action.payload;
    });
    builder.addCase(createOrder.rejected, (state, action) => {
      state.create.loading = false;
      state.create.error = action.payload as string;
    });

    // Fetch orders by organization
    builder.addCase(fetchOrdersByOrganization.pending, (state) => {
      state.byOrganization.loading = true;
      state.byOrganization.error = null;
    });
    builder.addCase(fetchOrdersByOrganization.fulfilled, (state, action) => {
      state.byOrganization.loading = false;
      state.byOrganization.data = action.payload;
    });
    builder.addCase(fetchOrdersByOrganization.rejected, (state, action) => {
      state.byOrganization.loading = false;
      state.byOrganization.error = action.payload as string;
    });

    // Fetch orders by parent organization
    builder.addCase(fetchOrdersByParentOrganization.pending, (state) => {
      state.byOrganization.loading = true;
      state.byOrganization.error = null;
    });
    builder.addCase(fetchOrdersByParentOrganization.fulfilled, (state, action) => {
      state.byOrganization.loading = false;
      state.byOrganization.data = action.payload;
    });
    builder.addCase(fetchOrdersByParentOrganization.rejected, (state, action) => {
      state.byOrganization.loading = false;
      state.byOrganization.error = action.payload as string;
    });

    // Update order status
    builder.addCase(updateOrderStatus.pending, (state) => {
      state.updateStatus.loading = true;
      state.updateStatus.error = null;
    });
    builder.addCase(updateOrderStatus.fulfilled, (state, action) => {
      state.updateStatus.loading = false;
      state.updateStatus.data = action.payload;
      // Also update the order in the byOrganization list
      if (state.byOrganization.data) {
        state.byOrganization.data = state.byOrganization.data.map((order) =>
          order.id === action.payload.id ? action.payload : order
        );
      }
      // And update single if it matches
      if (state.single.data && state.single.data.id === action.payload.id) {
        state.single.data = action.payload;
      }
    });
    builder.addCase(updateOrderStatus.rejected, (state, action) => {
      state.updateStatus.loading = false;
      state.updateStatus.error = action.payload as string;
    });
  },
});

export const { clearCreateOrderState } = orderSlice.actions;
export default orderSlice.reducer;

